'use client'

import { Button } from '@qr-front/ui/components/Button'
import { Dialog, DialogContent } from '@qr-front/ui/components/Dialog'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn } from '@qr-front/ui/utils'
import { showNotification } from 'components/Notification'
import { subscriptionApi } from 'features/purchase/subscription'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { getValidErrorMessage } from '@/utils/apiUtils'

export const CancelSubscriptionConfirmDialog = ({
	cancelRequestId,
	onOpenChange,
	onSuccess,
}: {
	cancelRequestId: string | null
	onOpenChange: (open: boolean) => void
	onSuccess: () => void
}) => {
	const t = useTranslations()
	const [isLoading, setIsLoading] = useState(false)

	const open = Boolean(cancelRequestId)

	const handleConfirm = async () => {
		if (!cancelRequestId || isLoading) return

		setIsLoading(true)
		try {
			await subscriptionApi.cancelPublicSubscription(cancelRequestId)
			onSuccess()
		} catch (error: unknown) {
			showNotification(getValidErrorMessage(error, t), { type: 'error' })
		} finally {
			setIsLoading(false)
		}
	}

	if (!open) {
		return null
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="p-6 tablet:p-8"
			>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-2">
						<h3 className="type-heading-3">
							{t('dashboard__billing__cancel_subscription_title')}
						</h3>
						<p className="type-body-medium">
							{t('dashboard__billing__cancel_subscription_description')}
						</p>
					</div>
					<div className="flex gap-4">
						<Button
							visual="secondary"
							className={cn('w-1/2', isLoading && 'pointer-events-none')}
							onClick={() => onOpenChange(false)}
						>
							{t('ui__generic__back')}
						</Button>
						<Button
							type="submit"
							className="w-1/2"
							onClick={handleConfirm}
							disabled={isLoading}
						>
							{t('ui__generic__confirm')}
							{isLoading && <Spinner color="white" />}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
